import { assertNever, CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { MoreVerticalIcon, TicketXIcon } from 'lucide-react'

import { NotWhitelistedDelegationWarning } from '../NotWhitelistedDelegationWarning'
import { Token } from '@/domain/types/Token'
import { assets } from '@/ui/assets'
import PointsBoostIcon from '@/ui/assets/points/points-boost-icon.svg?react'
import { Button } from '@/ui/atoms/button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { BlockExplorerAddressLink } from '@/ui/molecules/block-explorer-address-link/BlockExplorerAddressLink'
import { AmountCell } from '@/ui/molecules/data-table/components/AmountCell'
import { Info } from '@/ui/molecules/info/Info'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export type VotingPowerDelegationPanelProps =
  | ({
      variant: 'delegation'
    } & DelegationProps)
  | ({
      variant: 'no-delegation'
    } & NoDelegationProps)

export function VotingPowerDelegationPanel(props: VotingPowerDelegationPanelProps) {
  switch (props.variant) {
    case 'no-delegation':
      return <NoDelegation {...props} />
    case 'delegation':
      return <Delegation {...props} />
    default:
      assertNever(props)
  }
}

interface DelegationProps {
  openDelegateDialog: () => void
  openUndelegateDialog: () => void
  delegateAddress: CheckedAddress
  delegateAvatar?: string
  delegateName?: string
  delegatedVotingPower: NormalizedNumber
  spk: Token
  isDelegatingToNotWhitelisted: boolean
}

export function Delegation({
  openDelegateDialog,
  openUndelegateDialog,
  delegateName,
  delegateAddress,
  delegateAvatar,
  delegatedVotingPower,
  spk,
  isDelegatingToNotWhitelisted,
}: DelegationProps) {
  return (
    <Panel spacing="m" className="flex flex-col gap-9">
      <div className="flex flex-row items-center justify-between gap-3">
        <h5 className="typography-heading-6 sm:typography-heading-5 flex items-center gap-1">
          Voting power delegation
          <Info className="inline-flex sm:mt-1">
            By delegating your voting power, you authorize the delegate to vote on Spark governance proposals with the
            power of your tokens on your behalf, while you retain full ownership of the tokens.{' '}
            <ExternalLink to={links.docs.governance}>Learn more</ExternalLink>
          </Info>
        </h5>
        <Button
          variant="tertiary"
          size="s"
          onClick={openDelegateDialog}
          className="sm:hidden"
          data-testid={testIds.spkGovernance.votingPowerDelegationPanel.delegateButton}
        >
          Delegate
        </Button>
        <Button
          variant="tertiary"
          size="m"
          onClick={openDelegateDialog}
          className="hidden sm:block"
          data-testid={testIds.spkGovernance.votingPowerDelegationPanel.delegateButton}
        >
          Delegate
        </Button>
      </div>
      <ResponsiveDataTable
        gridTemplateColumnsClassName="grid-cols-[2fr_6fr_auto_auto] md:grid-cols-[2fr_6fr_auto_0]"
        data={[
          {
            openUndelegateDialog,
            delegateAddress,
            delegateAvatar,
            delegatedVotingPower,
            spk,
            isDelegatingToNotWhitelisted,
          },
        ]}
        columnDefinition={{
          position: {
            header: 'Delegate',
            renderCell: ({ delegateAddress, delegateAvatar }) => (
              <div
                className="flex items-center gap-2"
                data-testid={testIds.spkGovernance.votingPowerDelegationPanel.delegateAddress}
              >
                <img
                  src={delegateAvatar ?? assets.governance.unknownDelegateAvatar}
                  alt={delegateAddress}
                  className={cn('size-6 shrink-0 overflow-hidden rounded-full', !delegateAvatar && 'icon-secondary')}
                />
                {delegateName && <div className="typography-label-2 text-primary">{delegateName}</div>}
                <BlockExplorerAddressLink
                  address={delegateAddress}
                  compact
                  className="typography-label-2 text-secondary"
                />
              </div>
            ),
          },
          delegatedVotingPower: {
            header: 'Delegated voting power',
            headerAlign: 'right',
            renderCell: ({ delegatedVotingPower, spk, isDelegatingToNotWhitelisted }, mobileViewOptions) => {
              return (
                <div
                  className="flex items-center justify-end gap-1"
                  data-testid={testIds.spkGovernance.votingPowerDelegationPanel.delegatedVotingPower}
                >
                  <AmountCell
                    token={spk}
                    amount={delegatedVotingPower}
                    mobileViewOptions={mobileViewOptions}
                    iconSrc={assets.governance.votingPowerIcon}
                    formattingOptions={{
                      showUsdValue: false,
                      style: 'compact',
                      zeroAmountHandling: 'show-zero',
                    }}
                  />
                  {isDelegatingToNotWhitelisted && <NotWhitelistedDelegationWarning />}
                </div>
              )
            },
          },
          action: {
            header: '',
            headerAlign: 'right',
            renderCell: ({ openUndelegateDialog }, mobileViewOptions) => {
              if (mobileViewOptions) {
                return (
                  <Button
                    variant="tertiary"
                    size="s"
                    onClick={openUndelegateDialog}
                    className="w-full"
                    data-testid={testIds.spkGovernance.votingPowerDelegationPanel.undelegateButton}
                  >
                    Undelegate
                  </Button>
                )
              }
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      variant="tertiary"
                      size="s"
                      icon={MoreVerticalIcon}
                      data-testid={testIds.spkGovernance.votingPowerDelegationPanel.moreDropdown}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="flex cursor-pointer items-center gap-2 text-secondary"
                      onClick={openUndelegateDialog}
                      data-testid={testIds.spkGovernance.votingPowerDelegationPanel.undelegateButton}
                    >
                      <TicketXIcon className="icon-size-xs" />
                      Undelegate
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            },
          },
        }}
      />
    </Panel>
  )
}

interface NoDelegationProps {
  isConnected: boolean
  action: () => void
}

function NoDelegation({ isConnected, action }: NoDelegationProps) {
  return (
    <Panel spacing="m" className="flex flex-col gap-9">
      <div className="my-auto flex flex-col items-center gap-5">
        <PointsBoostIcon className="h-16" />
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="typography-heading-5 text-primary">
            {isConnected ? (
              'Delegate & Boost your Points'
            ) : (
              <>
                Connect Wallet, Delegate
                <br className="sm:hidden" /> & Boost your Points
              </>
            )}
          </div>
          <div className={cn('typography-body-3 text-secondary')}>
            {isConnected
              ? 'Delegate your voting power and increase your Spark points earnings by 10%.'
              : 'Connect your wallet to delegate your voting power and increase your Spark points earnings by 10%.'}
          </div>
        </div>
        <Button
          variant="primary"
          size="m"
          onClick={action}
          data-testid={
            isConnected
              ? testIds.spkGovernance.votingPowerDelegationPanel.noDelegationDelegateButton
              : testIds.spkGovernance.votingPowerDelegationPanel.noDelegationConnectWalletButton
          }
        >
          {isConnected ? 'Delegate now' : 'Connect Wallet'}
        </Button>
      </div>
    </Panel>
  )
}
